import {
  Badge,
  CopyIcon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EllipsisVerticalIcon,
  Table,
  TableCell,
  TablePager,
  TableRow,
} from '@fukuri-admins/ui/common';
import { iconButtonVariants } from '@fukuri-admins/ui/common/IconButton/logic';
import styles from './styles.module.scss';

/** ユーザーテーブルのソートキー */
type SortKey = 'no' | 'email' | 'name' | 'branchName' | 'branchCode' | 'createdAt';

/**
 * 利用者一覧テーブルの行データ型
 *
 * 表示整形済みの値を受け取る（converter で API 型から変換される）
 */
type UserRow = {
  /** 利用者の一意な識別子（API の user.id） */
  id: number;
  /** 表示順番号 */
  no: number;
  /** メールアドレス */
  email: string;
  /** 名前 */
  name: string;
  /** ログイン済みか（true: 完了 / false: 未完了） */
  isActivated: boolean;
  /**
   * アクティベーションコード
   *
   * isActivated が false の場合は具体的なコード文字列、true の場合は undefined
   */
  activationCode?: string;
  /** 事業所名 */
  branchName: string;
  /** 事業所コード */
  branchCode: string;
  /**
   * 利用開始日（表示整形済み）
   *
   * null の場合は '-' を格納する
   */
  billableFrom: string;
  /** 作成日時（表示整形済み） */
  createdAt: string;
};

type Props = {
  /** テーブルに表示する利用者行データ */
  rows: UserRow[];
  /**
   * ソートキー押下時のコールバック
   *
   * @param key ソート対象のキー
   */
  onSort: (key: SortKey) => void;
  /**
   * 内部向け列（ログイン・アクティベーションコード）を表示するか
   *
   * PartnerFeatureFlag の activation_code が有効かどうかの値を渡す
   */
  showInternalColumns: boolean;
  /**
   * 行の「編集」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onEditUser: (userId: number) => void;
  /**
   * 行の「削除」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onDeleteUser: (userId: number) => void;
  /**
   * 行クリック時のコールバック（利用者詳細画面への遷移）
   *
   * @param userId 操作対象の利用者 ID
   */
  onDetailClick: (userId: number) => void;
  /**
   * アクティベーションコードのコピーボタン押下時のコールバック
   *
   * @param code コピーするアクティベーションコード
   */
  onCopyActivationCode: (code: string) => void;
  /** ページャー props（currentPage / totalPage / itemCount / totalCount / onPageChange） */
  pagerProps: React.ComponentProps<typeof TablePager>;
};

/**
 * 利用者タブ用テーブル（テーブル本体 + ページャー）
 */
export const UsersTable: React.FC<Props> = ({
  rows,
  onSort,
  showInternalColumns,
  onEditUser,
  onDeleteUser,
  onDetailClick,
  onCopyActivationCode,
  pagerProps,
}) => (
  <>
    <Table
      scrollable
      headers={[
        { label: 'No', className: styles.noCol, sortable: true, onSort: () => onSort('no') },
        {
          label: '名前',
          className: styles.nameCol,
          sortable: true,
          onSort: () => onSort('name'),
        },
        {
          label: 'メールアドレス',
          className: styles.emailCol,
          sortable: true,
          onSort: () => onSort('email'),
        },
        ...(showInternalColumns
          ? [
              { label: 'ログイン', className: styles.loginCol },
              { label: 'アクティベーションコード', className: styles.activationCodeCol },
            ]
          : []),
        {
          label: '事業所',
          className: styles.branchNameCol,
          sortable: true,
          onSort: () => onSort('branchName'),
        },
        {
          label: '事業所コード',
          className: styles.branchCodeCol,
          sortable: true,
          onSort: () => onSort('branchCode'),
        },
        { label: '利用開始日', className: styles.billableFromCol },
        {
          label: '作成日時',
          className: styles.createdAtCol,
          sortable: true,
          onSort: () => onSort('createdAt'),
        },
        { label: '設定', className: styles.actionCol },
      ]}
    >
      {rows.map(row => (
        <TableRow key={row.id} onClick={() => onDetailClick(row.id)}>
          <TableCell>{row.no}</TableCell>
          <TableCell className={styles.nameCell}>{row.name}</TableCell>
          <TableCell className={styles.emailCell}>{row.email}</TableCell>
          {showInternalColumns && (
            <>
              <TableCell>
                <Badge
                  size='sm'
                  variant='subtle'
                  status={row.isActivated ? 'success' : 'neutral'}
                  label={row.isActivated ? '完了' : '未完了'}
                />
              </TableCell>
              <TableCell>
                {row.activationCode ? (
                  <div className={styles.activationCode}>
                    <span>{row.activationCode}</span>
                    <button
                      type='button'
                      aria-label='アクティベーションコードをコピー'
                      className={iconButtonVariants({ variant: 'ghost', size: 'sm' })}
                      onClick={e => {
                        e.stopPropagation();
                        onCopyActivationCode(row.activationCode ?? '');
                      }}
                    >
                      <CopyIcon size='xs' />
                    </button>
                  </div>
                ) : (
                  '-'
                )}
              </TableCell>
            </>
          )}
          <TableCell>{row.branchName}</TableCell>
          <TableCell>{row.branchCode}</TableCell>
          <TableCell>{row.billableFrom}</TableCell>
          <TableCell>{row.createdAt}</TableCell>
          <TableCell align='center'>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <button
                  type='button'
                  className={iconButtonVariants({ variant: 'ghost', size: 'sm' })}
                  aria-label='設定メニューを開く'
                  onClick={e => e.stopPropagation()}
                >
                  <EllipsisVerticalIcon />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' onClick={e => e.stopPropagation()}>
                <DropdownMenuItem onSelect={() => onEditUser(row.id)}>編集</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className={styles.deleteItem}
                  onSelect={() => onDeleteUser(row.id)}
                >
                  削除
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </TableCell>
        </TableRow>
      ))}
    </Table>
    <TablePager {...pagerProps} />
  </>
);
